import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PanToolAltIcon from '@mui/icons-material/PanToolAlt';
import PlaceIcon from '@mui/icons-material/Place';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { MapTools } from '../../context/WorkspaceContext';
import { useWorkspace } from '../../context/WorkspaceContext/useWorkspace';
import { useSettings } from '../../context/SettingsContext/useSettings';
import { useIntl } from 'react-intl';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    height: '30px',
    width: '26px',
  },
}));

const sectionDividerStyles = {
  width: '2px',
  alignSelf: 'stretch',
  mx: 0.75,
  backgroundColor: 'grey.500',
  borderRadius: 1,
};

interface ToolchainProps {
  disabled?: boolean;
}

const Toolchain = ({ disabled = false }: ToolchainProps) => {
  const intl = useIntl();
  const {
    store: { activeMapTool, objectSnappingEnabled },
    startDrawObject,
    setActiveMapTool,
    clearObjectDraft,
    toggleObjectSnapping,
    startBoxSelect,
  } = useWorkspace();
  const {
    store: { parentsVisibleOnMap },
    toggleParentsMapVisibility,
  } = useSettings();

  const handleDefault = () => {
    setActiveMapTool(null);
    clearObjectDraft();
  };

  const handleDraw = (tool: MapTools) => () => {
    startDrawObject(tool);
  };

  const handleBoxSelect = () => {
    startBoxSelect();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <StyledToggleButtonGroup
        value={activeMapTool}
        size="small"
        exclusive
        aria-label={intl.formatMessage({
          id: 'map.objectTools.ariaLabel',
          defaultMessage: 'Object map tools',
        })}
        disabled={disabled}
      >
        <ToggleButton
          title={intl.formatMessage({
            id: 'map.tool.defaultCursor',
            defaultMessage: 'Default map cursor',
          })}
          value={null as any}
          key="default"
          onClick={handleDefault}
        >
          <PanToolAltIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          title={intl.formatMessage({
            id: 'map.objectTools.drawPoint',
            defaultMessage: 'Draw point',
          })}
          value={MapTools.OBJECT_DRAW_POINT}
          key={MapTools.OBJECT_DRAW_POINT}
          onClick={handleDraw(MapTools.OBJECT_DRAW_POINT)}
        >
          <PlaceIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          title={intl.formatMessage({
            id: 'map.objectTools.drawLine',
            defaultMessage: 'Draw line',
          })}
          value={MapTools.OBJECT_DRAW_LINE}
          key={MapTools.OBJECT_DRAW_LINE}
          onClick={handleDraw(MapTools.OBJECT_DRAW_LINE)}
        >
          <ShowChartIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          title={intl.formatMessage({
            id: 'map.objectTools.drawPolygon',
            defaultMessage:
              'Draw polygon (click 3+ times, then save; ring is auto-closed)',
          })}
          value={MapTools.OBJECT_DRAW_POLYGON}
          key={MapTools.OBJECT_DRAW_POLYGON}
          onClick={handleDraw(MapTools.OBJECT_DRAW_POLYGON)}
        >
          <PentagonOutlinedIcon fontSize="small" />
        </ToggleButton>
        <ToggleButton
          title={intl.formatMessage({
            id: 'map.objectTools.drawCircle',
            defaultMessage: 'Draw circle (click centre, then edge)',
          })}
          value={MapTools.OBJECT_DRAW_CIRCLE}
          key={MapTools.OBJECT_DRAW_CIRCLE}
          onClick={handleDraw(MapTools.OBJECT_DRAW_CIRCLE)}
        >
          <CircleOutlinedIcon fontSize="small" />
        </ToggleButton>
      </StyledToggleButtonGroup>

      <Box sx={sectionDividerStyles} />

      <StyledToggleButtonGroup
        value={activeMapTool}
        size="small"
        exclusive
        aria-label={intl.formatMessage({
          id: 'map.objectTools.selectionAriaLabel',
          defaultMessage: 'Object selection tools',
        })}
        disabled={disabled}
      >
        <ToggleButton
          title={intl.formatMessage({
            id: 'map.objectTools.boxSelect',
            defaultMessage:
              'Box select (drag on map to select multiple objects)',
          })}
          value={MapTools.OBJECT_BOX_SELECT}
          key={MapTools.OBJECT_BOX_SELECT}
          onClick={handleBoxSelect}
        >
          <HighlightAltIcon fontSize="small" />
        </ToggleButton>
        <Tooltip
          title={
            parentsVisibleOnMap
              ? intl.formatMessage({
                  id: 'map.objectTools.parentsShown',
                  defaultMessage: 'Parent objects: shown on map',
                })
              : intl.formatMessage({
                  id: 'map.objectTools.parentsHidden',
                  defaultMessage: 'Parent objects: hidden on map',
                })
          }
        >
          <ToggleButton
            value="parents"
            key="parents"
            selected={parentsVisibleOnMap}
            onClick={toggleParentsMapVisibility}
          >
            {parentsVisibleOnMap ? (
              <AccountTreeIcon fontSize="small" />
            ) : (
              <AccountTreeOutlinedIcon fontSize="small" />
            )}
          </ToggleButton>
        </Tooltip>
        <Tooltip
          title={
            objectSnappingEnabled
              ? intl.formatMessage({
                  id: 'map.objectTools.snappingOn',
                  defaultMessage: 'Snapping: on (not wired yet — placeholder)',
                })
              : intl.formatMessage({
                  id: 'map.objectTools.snappingOff',
                  defaultMessage: 'Snapping: off (not wired yet — placeholder)',
                })
          }
        >
          <ToggleButton
            value="snap"
            key="snap"
            selected={objectSnappingEnabled}
            onClick={toggleObjectSnapping}
          >
            {objectSnappingEnabled ? (
              <LinkIcon fontSize="small" />
            ) : (
              <LinkOffIcon fontSize="small" />
            )}
          </ToggleButton>
        </Tooltip>
      </StyledToggleButtonGroup>
    </Box>
  );
};

export { Toolchain };
